import { ClockCircleOutlined, EditFilled } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';

import {
    ShiftListItemDto,
    ShiftTimeWorkedDto,
    ShiftTimeWorkedDtoTypeEnum,
} from '@clh/api-client';
import { STANDARD_MEAL_BREAK_MINUTES } from '@clh/util';

import { LogTime } from './log-time';
import { TimeWorkedSummary } from './time-worked-summary';

const timeWorkedTypeLabels: Record<ShiftTimeWorkedDtoTypeEnum, string> = {
    [ShiftTimeWorkedDtoTypeEnum.Standard]: 'Regular',
    [ShiftTimeWorkedDtoTypeEnum.OnCall]: 'On Call',
    [ShiftTimeWorkedDtoTypeEnum.CalledIn]: 'Called In',
};

function formatTime(date: Date, timezone?: string | null) {
    return DateTime.fromJSDate(
        date,
        timezone ? { zone: timezone } : undefined
    ).toFormat('HHmm ZZZZ');
}

function ClockInOutTooltip({
    timeWorked,
    timezone,
}: {
    timeWorked: ShiftTimeWorkedDto[];
    timezone?: string | null;
}) {
    const sorted = [...timeWorked].sort(
        (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );

    const content = (
        <div>
            {sorted.map((tw) => (
                <div key={tw.id}>
                    {formatTime(tw.startTime, timezone)} –{' '}
                    {formatTime(tw.endTime, timezone)}
                    <em> ({timeWorkedTypeLabels[tw.type] ?? tw.type})</em>
                </div>
            ))}
        </div>
    );

    return (
        <Tooltip title={content}>
            <ClockCircleOutlined style={{ marginLeft: 8, cursor: 'pointer' }} />
        </Tooltip>
    );
}

export const createSubmittedTimeFieldRenderer = (
    selection: ShiftListItemDto,
    isEditing: boolean,
    onEdit: () => void,
    onClose: () => void
): ((minutes?: number) => ReactNode) => {
    const renderSubmittedTime = (minutes?: number) => {
        const netWorkedMinutes = minutes ?? 0;
        const tookMealBreak =
            netWorkedMinutes > 0 &&
            selection.skippedMealBreak !== null &&
            !selection.skippedMealBreak;
        // shift.minutesWorked is NET (meal break already subtracted).
        // TimeWorkedSummary expects GROSS hours and subtracts meal break itself,
        // so we add it back here to avoid double subtraction.
        const mealBreakMinutes = tookMealBreak
            ? STANDARD_MEAL_BREAK_MINUTES
            : 0;
        const worked = (netWorkedMinutes + mealBreakMinutes) / 60;
        const onCall = selection.onCallShift?.minutesWorked
            ? selection.onCallShift.minutesWorked / 60
            : 0;

        const timezone = selection.facility.address.timezone;
        const hasTimeSubmitted = !!(minutes || onCall);
        const showMealBreakRow =
            netWorkedMinutes > 0 && selection.skippedMealBreak !== null;

        return (
            <div>
                {selection.nurseProfile && !isEditing ? (
                    <>
                        <a onClick={onEdit}>
                            {hasTimeSubmitted ? (
                                <>
                                    Edit Time <EditFilled />
                                </>
                            ) : (
                                'Submit Time'
                            )}
                        </a>
                        {selection.didNotWork && <em> Did not work</em>}
                        {!!selection.timeWorked?.length && hasTimeSubmitted && (
                            <ClockInOutTooltip
                                timeWorked={selection.timeWorked}
                                timezone={timezone}
                            />
                        )}
                    </>
                ) : null}

                {isEditing && <LogTime close={onClose} />}

                {hasTimeSubmitted && !isEditing && (
                    <>
                        <br />
                        <TimeWorkedSummary
                            didNotWork={selection.didNotWork}
                            worked={worked}
                            onCall={onCall}
                            tookMealBreak={tookMealBreak}
                        />
                        {showMealBreakRow && (
                            <div style={{ marginTop: 8 }}>
                                Meal break:{' '}
                                <b>
                                    {selection.skippedMealBreak ? 'No' : 'Yes'}
                                </b>
                                {selection.skippedMealBreak &&
                                    selection.skippedMealBreakComment && (
                                        <div>
                                            <em>
                                                {
                                                    selection.skippedMealBreakComment
                                                }
                                            </em>
                                        </div>
                                    )}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    renderSubmittedTime.displayName = 'SubmittedTimeFieldRenderer';
    return renderSubmittedTime;
};
